import { useRollbar } from '@rollbar/react';
import zipState from 'zip-state';
import useGlobalStore from '@/data-store/zustand/hooks/useGlobalStore';
import { asStringListFlagValue } from '@/utils/featureFlags/featureFlagHelpers';
import { isActiveZip, type ZipDataType } from '@/utils/zipUtility';

import useZipData from './useZipData';

export enum EligibilityType {
  SOLAR_ECOMMERCE = 'SOLAR_ECOMMERCE',
  WEBSITE = 'WEBSITE',
}

const websiteStates = [
  'CA',
  'CT',
  'FL',
  'HI',
  'IL',
  'MA',
  'MD',
  'NJ',
  'NV',
  'NY',
  'TX',
  'PR',
];

const useEligibility = () => {
  const { featureFlags } = useGlobalStore('featureFlags');
  const { zipData } = useZipData();
  const rollbar = useRollbar();

  const fetchEligibility = (
    eligibilityType: EligibilityType,
    zip: string
  ): boolean | null => {
    if (!zipData) {
      return null;
    }

    if (eligibilityType === EligibilityType.SOLAR_ECOMMERCE) {
      if (!featureFlags) {
        return null;
      }

      const ecommStates = asStringListFlagValue(
        featureFlags.SUNRUN_DOT_COM_QUOTE_STATES
      );
      if (!ecommStates) {
        return null;
      }
      return checkEligibility(ecommStates, zip, zipData);
    }

    return checkEligibility(websiteStates, zip, zipData);
  };

  const checkEligibility = (
    eligibleStates: string | string[],
    zip: string,
    zipData: ZipDataType
  ): boolean => {
    const state = zipState(zip);

    if (!state) {
      const errorMessage = `Could not determine state for zipcode: ${zip}`;
      console.log(errorMessage);
      rollbar.error(errorMessage);
      return false;
    }

    return (
      // biome-ignore lint/correctness/useParseIntRadix: Automated migration to Biome
      eligibleStates.includes(state) && isActiveZip(zipData, parseInt(zip))
    );
  };

  return {
    fetchEligibility,
  };
};

export default useEligibility;
